import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Fab,
  Tabs,
  Tab,
  Paper,
  Avatar
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Folder as FolderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  PeopleAlt as PeopleAltIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import projectService from '../services/projectService';
import ProjectDialog from '../components/common/ProjectDialog';
import BaseDialog from '../components/common/BaseDialog';
import BaseButton from '../components/common/BaseButton';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import userService from '../services/userService';

const ProjectsPage = () => {
  const { currentUser } = useAuth();
  const { addSystemNotification } = useNotifications();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tabValue, setTabValue] = useState('user');
  const [users, setUsers] = useState([]);
  const unsubscribeRef = useRef(null);

  const isAdmin = currentUser && (currentUser.profile?.isAdmin || currentUser.isAdmin);

  useEffect(() => {
    if (isAdmin) {
      userService.searchUsers('').then((usersList) => {
        setUsers(usersList);
      }).catch((error) => {
        console.error('Erreur chargement utilisateurs:', error);
      });
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!currentUser || !currentUser.uid) return;

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    setLoading(true);

    if (isAdmin && tabValue === 'all') {
      unsubscribeRef.current = projectService.onAllProjects((updatedProjects) => {
        setProjects(updatedProjects);
        setLoading(false);
      });
    } else {
      unsubscribeRef.current = projectService.onUserProjects(currentUser.uid, (updatedProjects) => {
        setProjects(updatedProjects);
        setLoading(false);
      });
    }

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [currentUser, tabValue, isAdmin]);

  useEffect(() => {
    const filtered = projects.filter(
      (project) =>
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredProjects(filtered);
  }, [projects, searchTerm]);

  const handleMenuOpen = (event, project) => {
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const handleOpenDialog = (project = null) => {
    setEditingProject(project);
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProject(null);
  };

  const handleViewProject = (projectId) => {
    navigate(`/projects/${projectId}`);
    handleMenuClose();
  };

  const handleDeleteProject = (project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;

    try {
      await projectService.deleteProject(projectToDelete.id);
      addSystemNotification('Projet supprimé avec succès', 'success');
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    } catch (error) {
      addSystemNotification('Erreur lors de la suppression du projet', 'error');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="60vh" textAlign="center" px={3}>
        <FolderIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Accès restreint
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
          Cette fonctionnalité est réservée aux administrateurs.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/meetings')} sx={{ mt: 3 }}>
          Retour aux réunions
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: isMobile ? 10 : 3 }}>
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FolderIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" fontWeight="bold">
              {tabValue === 'all' ? 'Tous les Projets' : 'Mes Projets'}
            </Typography>
          </Stack>
          {!isMobile && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Nouveau projet
            </Button>
          )}
        </Stack>

        {isAdmin && !isMobile && (
          <Paper elevation={0} sx={{
            mb: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden'
          }}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              variant="fullWidth"
              sx={{
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0'
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  minHeight: 56,
                  py: 2
                }
              }}
            >
              <Tab
                icon={<PersonIcon />}
                iconPosition="start"
                label="Mes projets"
                value="user"
                sx={{ gap: 1 }}
              />
              <Tab
                icon={<PeopleAltIcon />}
                iconPosition="start"
                label="Tous les projets"
                value="all"
                sx={{ gap: 1 }}
              />
            </Tabs>
          </Paper>
        )}

        <TextField
          fullWidth
          placeholder="Rechercher un projet..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ mb: 3 }}
        />
      </Box>

      {filteredProjects.length === 0 ? (
        <Box textAlign="center" py={8}>
          <FolderIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm ? 'Aucun projet trouvé' : 'Aucun projet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {searchTerm
              ? 'Essayez avec d\'autres mots-clés'
              : 'Créez votre premier projet pour regrouper vos réunions'}
          </Typography>
          {!searchTerm && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Créer un projet
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredProjects.map((project) => (
            <Grid item xs={12} sm={6} md={4} key={project.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  borderTop: `4px solid ${project.color || '#1976d2'}`,
                  '&:hover': {
                    boxShadow: 6,
                    cursor: 'pointer'
                  }
                }}
                onClick={() => handleViewProject(project.id)}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 2
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" flex={1}>
                      <FolderIcon sx={{ color: project.color || '#1976d2' }} />
                      <Typography variant="h6" noWrap>
                        {project.name}
                      </Typography>
                    </Stack>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, project);
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  {project.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {project.description}
                    </Typography>
                  )}

                  {isAdmin && tabValue === 'all' && (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.875rem' }}>
                        {users.find(u => u.id === project.userId)?.displayName?.charAt(0) || 'U'}
                      </Avatar>
                      <Typography variant="caption" color="text.secondary">
                        {users.find(u => u.id === project.userId)?.displayName ||
                         users.find(u => u.id === project.userId)?.email ||
                         'Utilisateur inconnu'}
                      </Typography>
                    </Box>
                  )}

                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    <Chip
                      size="small"
                      label={`${project.meetingIds?.length || 0} réunion${
                        (project.meetingIds?.length || 0) > 1 ? 's' : ''
                      }`}
                      color="primary"
                      variant="outlined"
                    />
                    {project.createdAt && (
                      <Chip
                        size="small"
                        label={format(new Date(project.createdAt), 'dd MMM yyyy', { locale: fr })}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {isMobile && (
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 80, right: 16 }}
          onClick={() => handleOpenDialog()}
        >
          <AddIcon />
        </Fab>
      )}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => selectedProject && handleViewProject(selectedProject.id)}>
          <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
          Voir les réunions
        </MenuItem>
        <MenuItem onClick={() => handleOpenDialog(selectedProject)}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Modifier
        </MenuItem>
        <MenuItem onClick={() => handleDeleteProject(selectedProject)}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" color="error" />
          <Typography color="error">Supprimer</Typography>
        </MenuItem>
      </Menu>

      <ProjectDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSuccess={() => {
          handleCloseDialog();
        }}
        projectId={editingProject?.id}
      />

      <BaseDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Supprimer le projet"
        actions={
          <>
            <BaseButton onClick={() => setDeleteDialogOpen(false)} color="inherit">
              Annuler
            </BaseButton>
            <BaseButton onClick={confirmDelete} color="error" variant="contained">
              Supprimer
            </BaseButton>
          </>
        }
      >
        <Typography>
          Êtes-vous sûr de vouloir supprimer le projet "{projectToDelete?.name}" ?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Les réunions associées ne seront pas supprimées, elles seront simplement dissociées du
          projet.
        </Typography>
      </BaseDialog>
    </Box>
  );
};

export default ProjectsPage;
